/**
 * Course Notes manager — staff/admin-only upload + delete UI.
 *
 * Lives BELOW the VideoManager on the Staff → Videos page, scoped by the
 * same brand tab. Installers see the resulting list of PDFs on
 * `/notes/:brand` which is view + download only.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Trash2, FileText, Download, ExternalLink, BookOpenText, Plus, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { useConfirm } from '@/components/ConfirmDialog';
import { formatApiError } from '@/utils/apiError';

function formatBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function CourseNotesManager({ brand, brandLabel, accent = '#D4A24B' }) {
  const confirm = useConfirm();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/course-notes/${brand}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load');
      setFiles(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brand]);

  useEffect(() => { load(); }, [load]);

  // Optimistically swap two files in local state, then persist the entire
  // top→bottom order to the server. If the server rejects we reload, so
  // the UI snaps back rather than drifting.
  const move = async (idx, direction) => {
    const target = idx + direction;
    if (target < 0 || target >= files.length || reordering) return;
    const next = [...files];
    [next[idx], next[target]] = [next[target], next[idx]];
    setFiles(next);
    setReordering(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/course-notes/${brand}/reorder`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: next.map(f => f.id) }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Reorder failed');
    } catch (e) {
      toast.error(e.message);
      await load(); // resync on failure
    } finally {
      setReordering(false);
    }
  };

  const onPickFile = () => fileInputRef.current?.click();

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // allow re-uploading the same filename later
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted.');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('PDF too large — 25 MB max.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/staff/course-notes/${brand}`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Upload failed');
      toast.success('Uploaded — installers will see it on their notes page.');
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (file) => {
    const ok = await confirm({
      title: `Delete “${file.filename}”?`,
      body: `This removes the file for everyone with ${brandLabel} access.`,
      confirmLabel: 'Delete', tone: 'danger',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/api/staff/course-notes/${brand}/file/${file.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      toast.success('Deleted.');
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const buttonStyle = { background: accent, color: brand === 'probound' ? '#0B0B0E' : '#fff' };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 mt-5" data-testid={`course-notes-manager-${brand}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <BookOpenText className="w-5 h-5" style={{ color: accent }} />
          <h2 className="text-base sm:text-lg font-bold text-gray-900">Course Notes &amp; Training Material</h2>
        </div>
        {!loading && files.length > 0 && (
          <Button
            onClick={onPickFile}
            disabled={uploading}
            size="sm"
            className="font-semibold"
            style={buttonStyle}
            data-testid={`course-notes-upload-another-${brand}`}
          >
            <Plus className="w-4 h-4 mr-1.5" />{uploading ? 'Uploading…' : 'Upload another'}
          </Button>
        )}
      </div>
      <p className="text-xs sm:text-sm text-gray-500 mb-4">
        Multiple {brandLabel} PDFs visible to every installer with {brandLabel} access on <code className="px-1 py-0.5 bg-gray-100 rounded text-[11px]">/notes/{brand}</code>.
      </p>

      {loading ? (
        <div className="py-8 text-center text-gray-500 text-sm">Loading…</div>
      ) : files.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center" data-testid={`course-notes-empty-${brand}`}>
          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl flex items-center justify-center" style={{ background: accent + '22', color: accent }}>
            <Upload className="w-7 h-7" />
          </div>
          <p className="text-sm font-semibold text-gray-900">No {brandLabel} course notes uploaded yet</p>
          <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">PDFs only · 25 MB max each · upload as many as you need.</p>
          <Button
            onClick={onPickFile}
            disabled={uploading}
            className="mt-4 font-semibold"
            data-testid={`course-notes-upload-empty-${brand}`}
            style={buttonStyle}
          >
            <Upload className="w-4 h-4 mr-1.5" />{uploading ? 'Uploading…' : 'Upload PDF'}
          </Button>
        </div>
      ) : (
        <div className="space-y-2" data-testid={`course-notes-list-${brand}`}>
          {files.map((f, idx) => {
            const previewUrl = `${API_URL}/api/course-notes/${brand}/file/${f.id}/download?t=${encodeURIComponent(f.uploaded_at || '')}`;
            const downloadUrl = `${API_URL}/api/course-notes/${brand}/file/${f.id}/download?download=1`;
            const isFirst = idx === 0;
            const isLast = idx === files.length - 1;
            return (
              <div key={f.id} className="flex flex-wrap items-center gap-3 p-3 border border-gray-200 rounded-xl" data-testid={`course-notes-row-${f.id}`}>
                <div className="flex flex-col gap-0.5 shrink-0">
                  <button
                    onClick={() => move(idx, -1)}
                    disabled={isFirst || reordering}
                    aria-label="Move up"
                    title="Move up"
                    data-testid={`course-notes-move-up-${f.id}`}
                    className="w-6 h-6 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => move(idx, +1)}
                    disabled={isLast || reordering}
                    aria-label="Move down"
                    title="Move down"
                    data-testid={`course-notes-move-down-${f.id}`}
                    className="w-6 h-6 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0" style={{ background: accent + '22', color: accent }}>
                  <FileText className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-gray-900 truncate" data-testid={`course-notes-filename-${f.id}`}>{f.filename}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {formatBytes(f.size_bytes)}
                    {f.uploaded_by_name && <> · uploaded by <strong className="text-gray-700">{f.uploaded_by_name}</strong></>}
                    {f.uploaded_at && <> · {new Date(f.uploaded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                  <Button asChild size="sm" variant="outline" data-testid={`course-notes-download-${f.id}`}>
                    <a href={downloadUrl} download={f.filename}><Download className="w-4 h-4 mr-1.5" />Download</a>
                  </Button>
                  <Button asChild size="sm" variant="outline" data-testid={`course-notes-open-${f.id}`}>
                    <a href={previewUrl} target="_blank" rel="noreferrer noopener"><ExternalLink className="w-4 h-4 mr-1.5" />Open</a>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(f)} data-testid={`course-notes-delete-${f.id}`} className="text-rose-700 border-rose-200 hover:bg-rose-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={handleUpload}
        style={{ display: 'none' }}
        data-testid={`course-notes-input-${brand}`}
      />
    </div>
  );
}
